import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Card } from '../../components/shared/Card';
import { Text } from '../../components/shared/Text';
import LocalStorage from '../../services/storage/LocalStorage';
import { Blueprint, BlueprintInterval, validateTrajectoryLanguage } from '../../models/Blueprint';
import BottomNavigation from '../../navigation/BottomNavigation';
import { PencilSimple, Calendar, Clock, MapPin, CaretRight, Sparkle, TrendUp, Sun, CaretDown, ThumbsUp, ThumbsDown, ArrowUp, ArrowDown, ChartLineUp, Target } from 'phosphor-react';
import { ReactionButtons } from '../../components/shared/ReactionButtons';
import { UserProfile, calculateProfileCompletion } from '../../models/UserProfile';
import { BirthDetails } from '../../models/BirthDetails';

/**
 * Your Blueprint Screen
 * Displays 60-day, 90-day, and long-term trajectory projections
 */
export const BlueprintScreen: React.FC = () => {
  const [blueprints, setBlueprints] = useState<Record<BlueprintInterval, Blueprint | null>>({
    '60-day': null,
    '90-day': null,
    'long-term': null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [scrollY, setScrollY] = useState(0);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [showEnergyPatternsTooltip, setShowEnergyPatternsTooltip] = useState(false);
  const [showPlanetCardTooltip, setShowPlanetCardTooltip] = useState(false);
  const [showProfileCompletionTooltip, setShowProfileCompletionTooltip] = useState(false);
  const [showPeakHoursTooltip, setShowPeakHoursTooltip] = useState(false);
  const [showGrowthTrajectoryTooltip, setShowGrowthTrajectoryTooltip] = useState(false);
  const [isStrengthsExpanded, setIsStrengthsExpanded] = useState(false);
  const [isGrowthTrajectoryExpanded, setIsGrowthTrajectoryExpanded] = useState(false);
  const [activeCyclesTab, setActiveCyclesTab] = useState<'60-day' | '90-day' | 'long-term'>('60-day');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Scroll to top when route changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo(0, 0);
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate opacity based on scroll position (fade out over 100px of scroll)
  const gradientOpacity = Math.max(0, 1 - scrollY / 100);

  // Helper function to abbreviate location names
  const abbreviateLocation = (location: string | undefined): string => {
    if (!location) return 'CA';
    
    const locationLower = location.toLowerCase().trim();
    
    // Handle "unknown" or empty values
    if (locationLower === 'unknown' || locationLower === '') {
      return 'CA';
    }
    
    // Common country/city abbreviations
    const abbreviations: Record<string, string> = {
      'singapore': 'SG',
      'united states': 'US',
      'united states of america': 'US',
      'usa': 'US',
      'united kingdom': 'UK',
      'great britain': 'UK',
      'australia': 'AU',
      'canada': 'CA',
      'california': 'CA',
      'new york': 'NYC',
      'los angeles': 'LA',
      'san francisco': 'SF',
      'london': 'LDN',
      'tokyo': 'TYO',
      'hong kong': 'HK',
      'sydney': 'SYD',
      'melbourne': 'MEL',
      'toronto': 'TOR',
      'vancouver': 'VAN',
    };
    
    // Check for exact match
    if (abbreviations[locationLower]) {
      return abbreviations[locationLower];
    }
    
    // For cities, try to abbreviate common patterns
    // If it's a multi-word city, use first letters (e.g., "New York" -> "NY")
    const words = location.split(/\s+/);
    if (words.length > 1 && words.length <= 3) {
      return words.map(w => w[0]?.toUpperCase() || '').join('');
    }
    
    // For single words longer than 8 characters, truncate to 4 chars
    if (location.length > 8) {
      return location.substring(0, 4).toUpperCase();
    }
    
    // Otherwise return as-is (already short enough)
    return location;
  };

  useEffect(() => {
    loadBlueprints();
    loadUserProfile();
  }, []);

  const loadUserProfile = () => {
    // Load user profile from localStorage
    const profile = LocalStorage.getItem<UserProfile>(LocalStorage.getKeys().USER_PROFILE);
    
    // Load birth details to merge with profile
    const birthDetails = LocalStorage.getItem<BirthDetails>(LocalStorage.getKeys().BIRTH_DETAILS);
    
    let mergedProfile: UserProfile = profile || {};
    
    // Merge birth details into profile if available
    if (birthDetails) {
      if (birthDetails.birthDate) {
        mergedProfile.dateOfBirth = typeof birthDetails.birthDate === 'string' 
          ? birthDetails.birthDate 
          : birthDetails.birthDate.toISOString();
      }
      if (birthDetails.birthTime) {
        mergedProfile.timeOfBirth = birthDetails.birthTime;
      }
      if (birthDetails.birthLocation) {
        mergedProfile.placeOfBirth = {
          city: birthDetails.birthLocation.city,
          country: birthDetails.birthLocation.country,
        };
      }
    }
    
    // Set default values if not set (mock data)
    if (!mergedProfile.fullName) {
      mergedProfile.fullName = 'Sean Lee';
    }
    if (!mergedProfile.dateOfBirth) {
      mergedProfile.dateOfBirth = '2000-05-07';
    }
    if (!mergedProfile.timeOfBirth) {
      mergedProfile.timeOfBirth = '14:35';
    }
    if (!mergedProfile.placeOfBirth) {
      mergedProfile.placeOfBirth = {
        city: 'Singapore',
        country: 'Singapore',
      };
    }
    
    setUserProfile(mergedProfile);
    setProfileCompletion(calculateProfileCompletion(mergedProfile));
  };

  const loadBlueprints = () => {
    setIsLoading(true);
    
    const intentions = LocalStorage.getItem<Array<{ id: string; text: string }>>(
      LocalStorage.getKeys().INTENTIONS
    ) || [];

    if (intentions.length === 0) {
      setIsLoading(false);
      return;
    }

    // Generate blueprints for each interval
    const intervals: BlueprintInterval[] = ['60-day', '90-day', 'long-term'];
    const newBlueprints: Record<BlueprintInterval, Blueprint | null> = {
      '60-day': null,
      '90-day': null,
      'long-term': null,
    };

    intervals.forEach((interval) => {
      const storageKey = `${LocalStorage.getKeys().BLUEPRINTS}:${interval}`;
      const stored = LocalStorage.getItem<Blueprint>(storageKey);

      if (stored && validateTrajectoryLanguage(stored.trajectoryNarrative)) {
        newBlueprints[interval] = stored;
      } else {
        // Generate new blueprint
        const narrative = generateTrajectoryNarrative(interval, intentions);
        if (validateTrajectoryLanguage(narrative)) {
          const blueprint: Blueprint = {
            id: crypto.randomUUID(),
            timeInterval: interval,
            trajectoryNarrative: narrative,
            patternBasis: ['intention_alignment', 'consistent_activity'],
            generatedAt: new Date().toISOString(),
            relatedIntentions: intentions.map((i) => i.id),
            conditionalLanguage: true,
          };
          newBlueprints[interval] = blueprint;
          LocalStorage.setItem(storageKey, blueprint);
        }
      }
    });

    setBlueprints(newBlueprints);
    setIsLoading(false);
  };

  const generateTrajectoryNarrative = (
    interval: BlueprintInterval,
    intentions: Array<{ text: string }>
  ): string => {
    const intentionText = intentions[0]?.text || 'your intentions';
    
    const narratives: Record<BlueprintInterval, string> = {
      '60-day': `If you continue at this pace, your alignment with ${intentionText} is developing into a consistent pattern. Based on your pattern data, by Day 60, your wellness practice is likely to become more automatic and integrated into your daily routine.`,
      '90-day': `If you maintain your current trajectory, your commitment to ${intentionText} is likely to develop into a sustainable habit. According to your patterns, by Day 90, you may notice significant growth in your alignment and consistency.`,
      'long-term': `If you continue on this path, your journey with ${intentionText} is likely to evolve into a deeply integrated part of your life. Based on your pattern data, your long-term trajectory shows potential for meaningful transformation and sustained alignment.`,
    };

    return narratives[interval];
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <Text variant="body" className="text-white-p60">
            Loading your blueprint...
          </Text>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  const hasBlueprints = Object.values(blueprints).some((bp) => bp !== null);

  const intervalLabels: Record<BlueprintInterval, string> = {
    '60-day': '60-Day Projection',
    '90-day': '90-Day Projection',
    'long-term': 'Long-Term Projection',
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Gradient header that fades on scroll */}
      <div 
        className="fixed top-0 left-0 right-0 h-[169px] z-0 pointer-events-none transition-opacity duration-300"
        style={{
          backgroundImage: 'url(/images/blueprint/headerimgblueprint.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: gradientOpacity,
        }}
      >
        {/* Gradient overlay */}
        <div 
          className="w-full h-full"
          style={{
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 1) 83.8%)',
          }}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 pb-0 overflow-hidden">
        <div ref={scrollContainerRef} className="relative z-20 pt-[50px] pb-[106px] h-full flex flex-col min-h-0 overflow-y-auto">
          {/* Settings bar - above header */}
          <div className="px-screen-padding mb-4 flex-shrink-0 flex justify-end" style={{ paddingBottom: '100px' }}>
            <button
              className="flex items-center gap-1.5 transition-opacity active:opacity-80"
              aria-label="Settings"
              onClick={() => {
                // TODO: Implement settings functionality
                console.log('Open settings');
              }}
            >
              <span
                style={{
                  fontFamily: 'Space Grotesk',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16.8px',
                  letterSpacing: '0.24px',
                  color: '#FFFFFF',
                }}
              >
                Settings
              </span>
              <CaretRight size={16} weight="regular" className="text-white-pure" />
            </button>
          </div>

          {/* Top bar - Blueprint header */}
          <div className="px-screen-padding mb-6 flex-shrink-0">
            <div className="relative flex items-start gap-4" style={{ minHeight: '100px' }}>
              
              {/* Left side - Profile picture */}
              <div className="flex-shrink-0">
                <div className="relative w-[100px] h-[100px]">
                  <div className="absolute inset-0 rounded-full overflow-hidden">
                    <img 
                      src="/images/profile/profilepicture.png" 
                      alt="Profile picture" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Edit icon overlay */}
                  <button
                    className="absolute bottom-0 right-0 w-[24px] h-[24px] rounded-full bg-black bg-opacity-60 flex items-center justify-center border border-white-pure cursor-pointer transition-opacity active:opacity-80"
                    style={{
                      backdropFilter: 'blur(4px)',
                    }}
                    onClick={() => {
                      // TODO: Implement profile image edit functionality
                      console.log('Edit profile image');
                    }}
                  >
                    <PencilSimple size={12} weight="regular" className="text-white-pure" />
                  </button>
                </div>
              </div>
              
              {/* Right side - User profile info */}
              <div className="flex-1 flex flex-col gap-3 pt-2">
                {/* Name */}
                <span
                  style={{
                    fontFamily: 'Didot, serif',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '24px',
                    lineHeight: '28.8px',
                    letterSpacing: '-0.48px',
                    color: '#FFFFFF',
                  }}
                >
                  Sean Lee
                </span>
                {/* Greeting */}
                <span
                  style={{
                    fontFamily: 'Space Grotesk',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '22.4px',
                    letterSpacing: '0.32px',
                    color: '#CCCCCC',
                  }}
                >
                  Good day, welcome back
                </span>
                
                {/* Personal info row */}
                <div className="flex items-center gap-6">
                  {/* Date */}
                  <div className="flex items-center gap-1.5">
                    <Calendar size={16} weight="regular" className="text-white-p60" />
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '16.8px',
                        letterSpacing: '0.24px',
                        color: '#FFFFFF',
                      }}
                    >
                      {userProfile?.dateOfBirth 
                        ? (() => {
                            const date = new Date(userProfile.dateOfBirth);
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            const year = date.getFullYear();
                            return `${month}-${day}-${year}`;
                          })()
                        : '05-07-2000'}
                    </span>
                  </div>
                  
                  {/* Time */}
                  <div className="flex items-center gap-1.5">
                    <Clock size={16} weight="regular" className="text-white-p60" />
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '16.8px',
                        letterSpacing: '0.24px',
                        color: '#FFFFFF',
                      }}
                    >
                      {userProfile?.timeOfBirth || '14:35'}
                    </span>
                  </div>
                  
                  {/* Location */}
                  <div className="flex items-center gap-1.5">
                    <MapPin size={16} weight="regular" className="text-white-p60" />
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '16.8px',
                        letterSpacing: '0.24px',
                        color: '#FFFFFF',
                      }}
                    >
                      {(() => {
                        if (userProfile?.placeOfBirth) {
                          const city = userProfile.placeOfBirth.city;
                          const country = userProfile.placeOfBirth.country;
                          
                          // If city and country are the same, just show one abbreviation
                          if (city && country && city.toLowerCase().trim() === country.toLowerCase().trim()) {
                            return abbreviateLocation(city);
                          }
                          
                          if (city && country) {
                            const cityAbbr = abbreviateLocation(city);
                            const countryAbbr = abbreviateLocation(country);
                            return `${cityAbbr}, ${countryAbbr}`;
                          } else if (city) {
                            return abbreviateLocation(city);
                          } else if (country) {
                            return abbreviateLocation(country);
                          }
                        }
                        return 'SG';
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Progression Card */}
          <div className="px-screen-padding mb-4 flex-shrink-0 relative">
            <div
              className="rounded-2xl relative"
              style={{
                padding: '16px',
                backgroundColor: 'rgba(254, 87, 6, 0.1)',
                border: '1px solid rgba(254, 87, 6, 0.3)',
                boxShadow: '0 0 20px rgba(254, 87, 6, 0.15), inset 0 0 20px rgba(254, 87, 6, 0.05)',
              }}
            >
              {/* Tooltip popup - positioned at top right of card */}
              {showProfileCompletionTooltip && (
                <div
                  className="absolute"
                  style={{
                    top: '16px',
                    right: '16px',
                    backgroundColor: '#010100',
                    borderRadius: '10px',
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    width: 'auto',
                    maxWidth: 'calc(100vw * 2 / 3)',
                    zIndex: 99999,
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '14px',
                      letterSpacing: '0.24px',
                      color: '#CCCCCC',
                    }}
                  >
                    Complete your profile by filling in: Full name, Date of birth, Place of birth, Gender, Occupation, and Interests. Time of birth is optional but recommended for more accurate insights.
                  </span>
                </div>
              )}
              <div className="flex flex-col gap-3 relative">
                {/* Progress bar */}
                <div className="w-full">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 600,
                          fontSize: '14px',
                          lineHeight: '19.6px',
                          color: '#FFFFFF',
                        }}
                      >
                        Profile Completion
                      </span>
                      {/* Tooltip icon */}
                      <button
                        onClick={() => {
                          setShowProfileCompletionTooltip(true);
                          // Auto-hide after 3 seconds
                          setTimeout(() => {
                            setShowProfileCompletionTooltip(false);
                          }, 3000);
                        }}
                        className="transition-opacity active:opacity-80"
                        style={{
                          width: '16px',
                          height: '16px',
                        }}
                        aria-label="Profile Completion information"
                      >
                        <img 
                          src="/svg/tooltip.svg" 
                          alt="Tooltip" 
                          className="w-4 h-4"
                        />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontSize: '16px',
                          lineHeight: '22.4px',
                          color: '#FE5706',
                        }}
                      >
                        {profileCompletion}%
                      </span>
                      {/* Right arrow button */}
                      <button
                        onClick={() => {
                          // TODO: Implement profile completion navigation
                          console.log('Navigate to profile completion');
                        }}
                        className="flex items-center justify-center transition-opacity active:opacity-80"
                        style={{
                          width: '24px',
                          height: '24px',
                        }}
                        aria-label="View profile completion"
                      >
                        <CaretRight size={16} weight="regular" className="text-white-pure" />
                      </button>
                    </div>
                  </div>
                  {/* Progress bar track - segmented style */}
                  <div className="w-full flex gap-1" style={{ height: '8px' }}>
                    {Array.from({ length: 5 }, (_, index) => {
                      // Each segment represents 20% (100% / 5 segments)
                      const segmentThreshold = (index + 1) * 20;
                      const isActive = profileCompletion >= segmentThreshold;
                      const isPartiallyActive = profileCompletion > index * 20 && profileCompletion < segmentThreshold;
                      
                      return (
                        <div
                          key={index}
                          className="flex-1 rounded"
                          style={{
                            backgroundColor: isActive 
                              ? 'transparent' 
                              : isPartiallyActive
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(255, 255, 255, 0.05)',
                            height: '100%',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          {(isActive || isPartiallyActive) && (
                            <div
                              className="absolute inset-0 rounded"
                              style={{
                                background: 'linear-gradient(to right, #FFA147, #FE5706)',
                                width: isPartiallyActive 
                                  ? `${((profileCompletion - index * 20) / 20) * 100}%`
                                  : '100%',
                                height: '100%',
                                transition: 'width 0.3s ease',
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Energy Patterns Card */}
          <div className="px-screen-padding mb-4 flex-shrink-0 relative">
            <Card variant="default" className="!px-4 !py-4 relative">
              {/* Tooltip popup - positioned at top right of card */}
              {showEnergyPatternsTooltip && (
                <div 
                  className="absolute"
                  style={{
                    top: '16px',
                    right: '16px',
                    backgroundColor: '#010100',
                    borderRadius: '10px',
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    width: 'auto',
                    maxWidth: 'calc(100vw * 2 / 3)',
                    zIndex: 99999,
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '14px',
                      letterSpacing: '0.24px',
                      color: '#CCCCCC',
                    }}
                  >
                    Peak (94%): Your highest energy window (6-10 AM) from birth chart and activity patterns. Growth (23%): Monthly progress vs last month from affirmations, intentions, and blueprint views. Alignment (87%): How well your current focus matches your set intentions.
                  </span>
                </div>
              )}
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '19.6px',
                        color: '#FFFFFF',
                      }}
                    >
                      Energy Patterns
                    </span>
                    {/* Tooltip icon */}
                    <button
                      onClick={() => {
                        setShowEnergyPatternsTooltip(true);
                        // Auto-hide after 3 seconds
                        setTimeout(() => {
                          setShowEnergyPatternsTooltip(false);
                        }, 3000);
                      }}
                      className="transition-opacity active:opacity-80"
                      style={{
                        width: '16px',
                        height: '16px',
                      }}
                      aria-label="Energy patterns info"
                    >
                      <img 
                        src="/svg/tooltip.svg" 
                        alt="Tooltip" 
                        className="w-4 h-4"
                      />
                    </button>
                  </div>
                  {/* 3 dots menu */}
                  <button
                    className="flex items-center justify-center gap-1 transition-opacity active:opacity-80"
                    style={{
                      width: '20px',
                      height: '8px',
                    }}
                    aria-label="More options"
                  >
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                  </button>
                </div>
                
                {/* Percentage-Based Energy Patterns - Each calculated independently out of 100% */}
                <div className="flex flex-col gap-4">
                  {(() => {
                    const patterns = [
                      { 
                        name: 'Peak', 
                        percentage: 94,
                        timeRange: '6-10 AM',
                        gradient: 'linear-gradient(to right, #FFA147, #FE5706)',
                        description: "Peak energy window",
                      },
                      { 
                        name: 'Growth', 
                        percentage: 23,
                        timeRange: 'This Month',
                        gradient: 'linear-gradient(to right, #6366F1, #8B5CF6)',
                        description: "vs last month",
                      },
                      { 
                        name: 'Alignment', 
                        percentage: 87,
                        timeRange: 'With Intentions',
                        gradient: 'linear-gradient(to right, #10B981, #14B8A6)',
                        description: "Current focus match",
                      }
                    ];

                    return patterns.map((pattern) => {
                      return (
                        <div key={pattern.name} className="flex flex-col gap-2">
                          {/* Header with name and percentage */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span
                                style={{
                                  fontFamily: 'Space Grotesk',
                                  fontStyle: 'normal',
                                  fontWeight: 400,
                                  fontSize: '14px',
                                  lineHeight: '19.6px',
                                  color: '#CCCCCC',
                                }}
                              >
                                {pattern.name}
                              </span>
                            </div>
                            <span
                              style={{
                                fontFamily: 'Space Grotesk',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                fontSize: '14px',
                                lineHeight: '19.6px',
                                color: '#FFFFFF',
                              }}
                            >
                              {pattern.percentage}%
                            </span>
                          </div>
                          
                          {/* Percentage bar - 3 segments */}
                          <div className="relative w-full flex gap-1" style={{ height: '8px' }}>
                            {[0, 1, 2].map((segmentIndex) => {
                              // Each segment represents ~33.33% of the bar
                              const segmentThreshold = (segmentIndex + 1) * 33.33;
                              const prevThreshold = segmentIndex * 33.33;
                              const isFilled = pattern.percentage >= segmentThreshold;
                              const isPartiallyFilled = pattern.percentage > prevThreshold && pattern.percentage < segmentThreshold;
                              
                              // Calculate fill percentage within this segment
                              const segmentFillPercent = isPartiallyFilled 
                                ? ((pattern.percentage - prevThreshold) / 33.33) * 100 
                                : (isFilled ? 100 : 0);
                              
                              return (
                                <div 
                                  key={segmentIndex}
                                  className="relative flex-1 rounded-full overflow-hidden"
                                  style={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  }}
                                >
                                  {(isFilled || isPartiallyFilled) && (
                                    <div
                                      className="absolute inset-y-0 left-0 rounded-full"
                                      style={{
                                        width: `${segmentFillPercent}%`,
                                        background: pattern.gradient,
                                        boxShadow: `0 0 12px ${pattern.name === 'Peak' ? 'rgba(254, 87, 6, 0.4)' : pattern.name === 'Growth' ? 'rgba(139, 92, 246, 0.4)' : 'rgba(20, 184, 166, 0.4)'}`,
                                      }}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* Time range and description */}
                          <span
                            style={{
                              fontFamily: 'Space Grotesk',
                              fontStyle: 'normal',
                              fontWeight: 400,
                              fontSize: '12px',
                              lineHeight: '16.8px',
                              color: '#999999',
                            }}
                          >
                            {pattern.timeRange} • {pattern.description}
                          </span>
                        </div>
                      );
                    });
                  })()}
                </div>
                
                {/* Reaction buttons */}
                <div style={{ marginTop: '16px' }}>
                  <ReactionButtons />
                </div>
              </div>
            </Card>
          </div>

          {/* Detailed Growth Trajectory Card */}
          <div className="px-screen-padding mb-4 flex-shrink-0">
            <Card 
              variant="default" 
              className="flex-shrink-0 !px-3 !py-4 relative"
            >
              <div className="flex flex-col gap-4">
                {/* Title and 3-dots menu */}
                <div className="flex items-center justify-between">
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '16.8px',
                      letterSpacing: '0.28px',
                      color: '#E6E6E6',
                    }}
                  >
                    Growth Opportunities
                  </span>
                  {/* 3 dots menu */}
                  <button
                    className="flex items-center justify-center gap-1 transition-opacity active:opacity-80"
                    style={{
                      width: '20px',
                      height: '8px',
                    }}
                    aria-label="More options"
                  >
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                  </button>
                </div>
                
                {/* Image card with background */}
                <div
                  className="rounded-[10px] overflow-hidden relative"
                  style={{
                    height: '162px',
                    backgroundImage: 'url(/images/blueprint/growthoppotunities.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                  {/* Soft black overlay */}
                  <div 
                    className="absolute inset-0"
                    style={{
                      background: 'rgba(0, 0, 0, 0.4)',
                    }}
                  />
                </div>
                
                {/* Description */}
                <div>
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'italic',
                      fontWeight: 400,
                      fontSize: '11px',
                      lineHeight: '14px',
                      letterSpacing: '0.22px',
                      color: 'rgba(255, 255, 255, 0.6)',
                    }}
                  >
                    Discover pathways for deeper growth. Explore how your current patterns can evolve into transformative opportunities.
                  </span>
                </div>
                
                {/* If You Continue section */}
                <div className="flex flex-col gap-2">
                  <div>
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: '14.4px',
                        letterSpacing: '0.24px',
                        color: '#E6E6E6',
                      }}
                    >
                      If You Continue...
                    </span>
                  </div>
                  <div>
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '19.6px',
                        letterSpacing: '0.28px',
                        color: '#CCCCCC',
                      }}
                    >
                      Your consistent practice is building momentum. Each day strengthens your alignment with your intentions, creating a foundation for deeper transformation.
                    </span>
                  </div>
                </div>
                
                {/* Growth Potential section - Only shown when expanded */}
                {isGrowthTrajectoryExpanded && (
                  <div className="flex flex-col gap-2">
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: '#E6E6E6',
                        }}
                      >
                        Growth Potential
                      </span>
                    </div>
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '19.6px',
                          letterSpacing: '0.28px',
                          color: '#CCCCCC',
                        }}
                      >
                        Your patterns indicate strong potential for sustainable growth. The opportunities ahead align with your natural rhythms and intentions.
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Next Steps section - Only shown when expanded */}
                {isGrowthTrajectoryExpanded && (
                  <div className="flex flex-col gap-2">
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: '#E6E6E6',
                        }}
                      >
                        Next Steps
                      </span>
                    </div>
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '19.6px',
                          letterSpacing: '0.28px',
                          color: '#CCCCCC',
                        }}
                      >
                        Focus on maintaining your current rhythm. Small, consistent actions compound into meaningful change over time.
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Reaction buttons with expand button */}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <button
                      className="flex items-center justify-center rounded-full transition-opacity active:opacity-80"
                      style={{
                        width: '30px',
                        height: '30px',
                        backgroundColor: '#272727',
                      }}
                      aria-label="Ask GodGPT"
                    >
                      <img 
                        src="/svg/godgpticon.svg" 
                        alt="GodGPT Icon" 
                        className="w-4 h-4"
                      />
                    </button>
                    <button
                      className="flex items-center justify-center rounded-full transition-opacity active:opacity-80"
                      style={{
                        width: '30px',
                        height: '30px',
                        backgroundColor: '#272727',
                      }}
                      aria-label="Thumbs up"
                    >
                      <ThumbsUp size={16} weight="regular" className="text-white-pure" />
                    </button>
                    <button
                      className="flex items-center justify-center rounded-full transition-opacity active:opacity-80"
                      style={{
                        width: '30px',
                        height: '30px',
                        backgroundColor: '#272727',
                      }}
                      aria-label="Thumbs down"
                    >
                      <ThumbsDown size={16} weight="regular" className="text-white-pure" />
                    </button>
                  </div>
                  {/* Expand/Collapse button */}
                  <button
                    onClick={() => setIsGrowthTrajectoryExpanded(!isGrowthTrajectoryExpanded)}
                    className="rounded-full flex items-center justify-center transition-opacity active:opacity-80"
                    style={{
                      width: '40px',
                      height: '30px',
                      background: 'rgb(39, 39, 39)',
                      borderRadius: '99px',
                    }}
                    aria-label={isGrowthTrajectoryExpanded ? 'Collapse' : 'Expand'}
                  >
                    <CaretDown 
                      size={16} 
                      weight="regular" 
                      className="text-white-pure"
                      style={{
                        transform: isGrowthTrajectoryExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                      }}
                    />
                  </button>
                </div>
              </div>
            </Card>
          </div>

          {/* New Card - Intention Card Layout */}
          <div className="px-screen-padding mb-4 flex-shrink-0 relative">
            <Card
              variant="elevated"
              className="cursor-pointer h-full flex flex-col justify-center overflow-hidden relative"
              style={{
                minHeight: '400px',
                height: '100%',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                padding: '50px',
              }}
              overlay={
                <>
                  <div
                    className="absolute inset-0 pointer-events-none rounded-2xl"
                    style={{
                      backgroundImage: 'url(/images/blueprint/planetcard.png)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      zIndex: 1,
                    }}
                  />
                  <div
                    className="absolute inset-0 pointer-events-none rounded-2xl"
                    style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      zIndex: 2,
                    }}
                  />
                  {/* Tooltip icon - positioned at top right of card */}
                  <button
                    onClick={() => {
                      setShowPlanetCardTooltip(true);
                      // Auto-hide after 3 seconds
                      setTimeout(() => {
                        setShowPlanetCardTooltip(false);
                      }, 3000);
                    }}
                    className="absolute transition-opacity active:opacity-80"
                    style={{
                      top: '16px',
                      right: '16px',
                      width: '16px',
                      height: '16px',
                      zIndex: 100,
                    }}
                    aria-label="Planet card information"
                  >
                    <img 
                      src="/svg/tooltip.svg" 
                      alt="Tooltip" 
                      className="w-4 h-4"
                      style={{
                        filter: 'brightness(0) invert(1)',
                      }}
                    />
                  </button>
                  {/* Tooltip popup - positioned at top right of card */}
                  {showPlanetCardTooltip && (
                    <div 
                      className="absolute"
                      style={{
                        top: '16px',
                        right: '16px',
                        backgroundColor: '#010100',
                        borderRadius: '10px',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        width: 'auto',
                        maxWidth: 'calc(100vw * 2 / 3)',
                        zIndex: 99999,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '14px',
                          letterSpacing: '0.24px',
                          color: '#CCCCCC',
                        }}
                      >
                        This information is derived from your birth chart data (date, time, and place of birth) combined with your current intentions. The system analyzes planetary alignments and their relationship to your personal goals to provide personalized insights.
                      </span>
                    </div>
                  )}
                </>
              }
            >
              <div className="flex flex-col gap-8 relative z-10">
                {/* Title */}
                <div className="flex items-center justify-center">
                  <span
                    style={{
                      fontFamily: 'Didot, serif',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '24px',
                      lineHeight: '1.5',
                      color: '#FFFFFF',
                      textAlign: 'center',
                      textTransform: 'uppercase',
                    }}
                  >
                    Based On Your Universe
                  </span>
                </div>
                
                {/* Body text */}
                <span
                  style={{
                    fontFamily: 'Space Grotesk',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '19.6px',
                    color: '#CCCCCC',
                    textAlign: 'center',
                  }}
                >
                  The Moon is currently aligned in your chart, heightening your emotional intuition and amplifying your intentions. Your birth chart reveals natural rhythms of reflection and growth.
                </span>
                
                {/* Ask and reflect button */}
                <div className="flex justify-center relative z-10 flex-shrink-0">
                  <button
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded-[15px] border border-white-p90 h-9 transition-all active:bg-white-pure [&:active_span]:!text-black [&:active_span]:!opacity-100 [&:active>div>img]:brightness-0"
                    onClick={() => {
                      console.log('Button clicked');
                    }}
                  >
                    <span
                      className="transition-colors"
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '28px',
                        color: '#E6E6E6',
                      }}
                    >
                      Ask and reflect with
                    </span>
                    <div className="flex items-center gap-1.5">
                      <img 
                        src="/svg/godgpticon.svg" 
                        alt="GodGPT Icon" 
                        className="w-[13px] h-4 transition-all group-active:brightness-0"
                      />
                      <span
                        className="transition-colors"
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '28px',
                          color: '#E6E6E6',
                        }}
                      >
                        GodGPT
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </Card>
          </div>

          {/* Your Strengths Card */}
          <div className="px-screen-padding mb-4 flex-shrink-0">
            <Card 
              variant="default" 
              className="flex-shrink-0 !px-3 !py-4 relative"
            >
              <div className="flex flex-col gap-4">
                {/* Title and 3-dots menu */}
                <div className="flex items-center justify-between">
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '16.8px',
                      letterSpacing: '0.28px',
                      color: '#E6E6E6',
                    }}
                  >
                    Your Strengths
                  </span>
                  {/* 3 dots menu */}
                  <button
                    className="flex items-center justify-center gap-1 transition-opacity active:opacity-80"
                    style={{
                      width: '20px',
                      height: '8px',
                    }}
                    aria-label="More options"
                  >
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                  </button>
                </div>
                
                {/* Image card with background */}
                <div
                  className="rounded-[10px] overflow-hidden relative"
                  style={{
                    height: '162px',
                    backgroundImage: 'url(/images/blueprint/yourstrengths.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                  {/* Soft black overlay */}
                  <div 
                    className="absolute inset-0"
                    style={{
                      background: 'rgba(0, 0, 0, 0.4)',
                    }}
                  />
                </div>
                
                {/* Description */}
                <div>
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'italic',
                      fontWeight: 400,
                      fontSize: '11px',
                      lineHeight: '14px',
                      letterSpacing: '0.22px',
                      color: 'rgba(255, 255, 255, 0.6)',
                    }}
                  >
                    You evolve in layers. Discover the unique strengths that define your growth journey.
                  </span>
                </div>
                
                {/* Strengths section */}
                <div className="flex flex-col gap-2">
                  <div>
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: '14.4px',
                        letterSpacing: '0.24px',
                        color: '#E6E6E6',
                      }}
                    >
                      Strengths
                    </span>
                  </div>
                  <div>
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '19.6px',
                        letterSpacing: '0.28px',
                        color: '#CCCCCC',
                      }}
                    >
                      1. Communication + Intuition Fusion
                    </span>
                  </div>
                  <div>
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '19.6px',
                        letterSpacing: '0.28px',
                        color: '#CCCCCC',
                      }}
                    >
                      2. Gemini gives you broadcast power, Scorpio Moon gives you x-ray insight.
                    </span>
                  </div>
                </div>
                
                {/* Your Inner Power section - Only shown when expanded */}
                {isStrengthsExpanded && (
                  <div className="flex flex-col gap-2">
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: '#E6E6E6',
                        }}
                      >
                        Your Inner Power
                      </span>
                    </div>
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '19.6px',
                          letterSpacing: '0.28px',
                          color: '#CCCCCC',
                        }}
                      >
                        Your power grows when you balance intuition and logic.
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Growth section - Only shown when expanded */}
                {isStrengthsExpanded && (
                  <div className="flex flex-col gap-2">
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: '#E6E6E6',
                        }}
                      >
                        Growth
                      </span>
                    </div>
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '19.6px',
                          letterSpacing: '0.28px',
                          color: '#CCCCCC',
                        }}
                      >
                        1. Overthinking / Overanalyzing
                      </span>
                    </div>
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '19.6px',
                          letterSpacing: '0.28px',
                          color: '#CCCCCC',
                        }}
                      >
                        2. Virgo + Gemini can trap you in mental loops.
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Your Core Patterns section - Only shown when expanded */}
                {isStrengthsExpanded && (
                  <div className="flex flex-col gap-2">
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: '#E6E6E6',
                        }}
                      >
                        Your Core Patterns
                      </span>
                    </div>
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '19.6px',
                          letterSpacing: '0.28px',
                          color: '#CCCCCC',
                        }}
                      >
                        You grasp patterns beneath the surface and express them with clarity. You are both mirror and messenger.
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Reaction buttons with expand button */}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <button
                      className="flex items-center justify-center rounded-full transition-opacity active:opacity-80"
                      style={{
                        width: '30px',
                        height: '30px',
                        backgroundColor: '#272727',
                      }}
                      aria-label="Ask GodGPT"
                    >
                      <img 
                        src="/svg/godgpticon.svg" 
                        alt="GodGPT Icon" 
                        className="w-4 h-4"
                      />
                    </button>
                    <button
                      className="flex items-center justify-center rounded-full transition-opacity active:opacity-80"
                      style={{
                        width: '30px',
                        height: '30px',
                        backgroundColor: '#272727',
                      }}
                      aria-label="Thumbs up"
                    >
                      <ThumbsUp size={16} weight="regular" className="text-white-pure" />
                    </button>
                    <button
                      className="flex items-center justify-center rounded-full transition-opacity active:opacity-80"
                      style={{
                        width: '30px',
                        height: '30px',
                        backgroundColor: '#272727',
                      }}
                      aria-label="Thumbs down"
                    >
                      <ThumbsDown size={16} weight="regular" className="text-white-pure" />
                    </button>
                  </div>
                  {/* Expand button */}
                  <button
                    onClick={() => setIsStrengthsExpanded(!isStrengthsExpanded)}
                    className="rounded-full flex items-center justify-center transition-opacity active:opacity-80"
                    style={{
                      width: '40px',
                      height: '30px',
                      background: 'rgb(39, 39, 39)',
                      borderRadius: '99px',
                    }}
                    aria-label={isStrengthsExpanded ? 'Collapse' : 'Expand'}
                  >
                    <CaretDown 
                      size={16} 
                      weight="regular" 
                      className="text-white-pure"
                      style={{
                        transform: isStrengthsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                      }}
                    />
                  </button>
                </div>
              </div>
            </Card>
          </div>

          {/* Cycles Card */}
          <div className="px-screen-padding flex-shrink-0">
            <Card variant="default" className="flex-shrink-0 !px-3 !py-4 relative">
              <div className="flex flex-col gap-4">
                {/* Title and 3-dots menu */}
                <div className="flex items-center justify-between">
                  <span
                    style={{
                      fontFamily: 'Space Grotesk',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '16.8px',
                      letterSpacing: '0.28px',
                      color: '#E6E6E6',
                    }}
                  >
                    Your Cycles
                  </span>
                  {/* 3 dots menu */}
                  <button
                    className="flex items-center justify-center gap-1 transition-opacity active:opacity-80"
                    style={{
                      width: '20px',
                      height: '8px',
                    }}
                    aria-label="More options"
                  >
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                    <div
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%',
                        backgroundColor: '#CCCCCC',
                      }}
                    />
                  </button>
                </div>
                
                {/* Tabs */}
                <div className="flex gap-4">
                  {/* 60 Day Tab */}
                  <div
                    className={`flex-1 rounded-[15px] overflow-hidden relative cursor-pointer transition-opacity ${activeCyclesTab === '60-day' ? 'border border-white-p60' : ''}`}
                    style={{
                      height: '160px',
                      backgroundImage: 'url(/images/blueprint/your_cycles/60day.png)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      opacity: activeCyclesTab === '60-day' ? 1 : 0.5,
                    }}
                    onClick={() => setActiveCyclesTab('60-day')}
                  >
                    {/* Overlay for text readability */}
                    <div 
                      className="absolute inset-0"
                      style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                      }}
                    />
                    <div className="p-3 flex flex-col gap-2 relative z-10 h-full justify-end">
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: activeCyclesTab === '60-day' ? '#E6E6E6' : '#666666',
                        }}
                      >
                        60 Day
                      </span>
                    </div>
                  </div>
                  
                  {/* 90 Day Tab */}
                  <div
                    className={`flex-1 rounded-[15px] overflow-hidden relative cursor-pointer transition-opacity ${activeCyclesTab === '90-day' ? 'border border-white-p60' : ''}`}
                    style={{
                      height: '160px',
                      backgroundImage: 'url(/images/blueprint/your_cycles/90day.png)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      opacity: activeCyclesTab === '90-day' ? 1 : 0.5,
                    }}
                    onClick={() => setActiveCyclesTab('90-day')}
                  >
                    {/* Overlay for text readability */}
                    <div 
                      className="absolute inset-0"
                      style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                      }}
                    />
                    <div className="p-3 flex flex-col gap-2 relative z-10 h-full justify-end">
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: activeCyclesTab === '90-day' ? '#E6E6E6' : '#666666',
                        }}
                      >
                        90 Day
                      </span>
                    </div>
                  </div>
                  
                  {/* Long Term Tab */}
                  <div
                    className={`flex-1 rounded-[15px] overflow-hidden relative cursor-pointer transition-opacity ${activeCyclesTab === 'long-term' ? 'border border-white-p60' : ''}`}
                    style={{
                      height: '160px',
                      backgroundImage: 'url(/images/blueprint/your_cycles/longterm.png)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      opacity: activeCyclesTab === 'long-term' ? 1 : 0.5,
                    }}
                    onClick={() => setActiveCyclesTab('long-term')}
                  >
                    {/* Overlay for text readability */}
                    <div 
                      className="absolute inset-0"
                      style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                      }}
                    />
                    <div className="p-3 flex flex-col gap-2 relative z-10 h-full justify-end">
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: activeCyclesTab === 'long-term' ? '#E6E6E6' : '#666666',
                        }}
                      >
                        Long Term
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Detail Section */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontSize: '12px',
                          lineHeight: '14.4px',
                          letterSpacing: '0.24px',
                          color: '#E6E6E6',
                        }}
                      >
                        {activeCyclesTab === '60-day' ? '60 Day' : activeCyclesTab === '90-day' ? '90 Day' : 'Long Term'}
                      </span>
                    </div>
                    
                    {/* Body text below title */}
                    <div>
                      <span
                        style={{
                          fontFamily: 'Space Grotesk',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '19.6px',
                          letterSpacing: '0.28px',
                          color: '#CCCCCC',
                        }}
                      >
                        {activeCyclesTab === '60-day' 
                          ? "This initial phase focuses on establishing consistent daily practices and building foundational habits that support your intentions."
                          : activeCyclesTab === '90-day'
                          ? "This period emphasizes deeper integration of your practices, where patterns become more refined and progress becomes more visible."
                          : "This extended timeframe represents the transformation of practices into sustainable systems that naturally support your ongoing growth journey."
                        }
                      </span>
                    </div>
                  </div>
                  
                  {/* Current Cycle */}
                  <div className="flex flex-col gap-2">
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: '14.4px',
                        letterSpacing: '0.24px',
                        color: '#E6E6E6',
                      }}
                    >
                      Current Cycle
                    </span>
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '19.6px',
                        letterSpacing: '0.28px',
                        color: '#CCCCCC',
                      }}
                    >
                      {activeCyclesTab === '60-day' 
                        ? "Days 1-60 (Building Foundation)"
                        : activeCyclesTab === '90-day'
                        ? "Days 61-90 (Deepening Integration)"
                        : "Beyond 90 Days (Sustainable Growth)"
                      }
                    </span>
                  </div>
                  
                  {/* Personal Growth Summary */}
                  <div className="flex flex-col gap-2">
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: '14.4px',
                        letterSpacing: '0.24px',
                        color: '#E6E6E6',
                      }}
                    >
                      Personal Growth Summary
                    </span>
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '19.6px',
                        letterSpacing: '0.28px',
                        color: '#CCCCCC',
                      }}
                    >
                      {activeCyclesTab === '60-day' 
                        ? "Current cycle: building momentum & clarity"
                        : activeCyclesTab === '90-day'
                        ? "Current cycle: deepening integration & progress"
                        : "Current cycle: sustainable alignment & transformation"
                      }
                    </span>
                  </div>
                  
                  {/* What It Means For You */}
                  <div className="flex flex-col gap-2">
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: '14.4px',
                        letterSpacing: '0.24px',
                        color: '#E6E6E6',
                      }}
                    >
                      What It Means For You?
                    </span>
                    <span
                      style={{
                        fontFamily: 'Space Grotesk',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '19.6px',
                        letterSpacing: '0.28px',
                        color: '#CCCCCC',
                      }}
                    >
                      {activeCyclesTab === '60-day' 
                        ? "If you continue with your affirmations and intentions at this pace, you're likely to see clearer patterns in your daily alignment. Your focus and consistency will likely strengthen, helping you build momentum toward your goals."
                        : activeCyclesTab === '90-day'
                        ? "Based on your pattern data, if you maintain your current practice, you're likely to develop deeper integration of your intentions. Your growth trajectory suggests increased clarity and more tangible progress toward your wellness goals."
                        : "If you continue this journey long-term, your patterns indicate you're likely to develop a sustainable alignment system. Your consistent practice will likely transform into natural habits that support your ongoing growth and well-being."
                      }
                    </span>
                  </div>
                </div>
                
                {/* Reaction buttons */}
                <ReactionButtons />
              </div>
            </Card>
          </div>

        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default BlueprintScreen;
